import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, DialogTitle } from '@mui/material';

import { loadTimelineTypeDetails } from '../../store/directusService';
import { createParticipantTimelineEvent } from '../../store/participantService';
import { getConfiguredItems } from '../../utils/directusFunctions';
import { clearKeys } from '../../utils/objectUtils';
import { hasRole } from '../../utils/userRoles';
import Button from '../formElements/Button';
import DateSelect from '../formElements/DateSelect';
import SingleSelect from '../formElements/SingleSelect';
import TextAreaField from '../formElements/TextAreaField';
import TimeSelect from '../formElements/TimeSelect';

import { NOTE_CHAR_LIMIT, validate } from './validateTimelineEditor';

import form from '../../commonStyles/formStyles.module.css';
import modal from '../../commonStyles/modal.module.css';
import classes from './timelineStyles/timeline.module.css';

const TimelineEditor = ({ isOpen, acceptedRoles, onOpenTimelineEditor, timelineStatusDetails }) => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        eventDate: '',
        eventTime: '',
        notes: '',
        eventStatusId: '',
        itemTypeId: '',
        participantId: '',
        userId: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [arrayTimelineTypes, setArrayTimelineTypes] = useState([]);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [errors, setErrors] = useState({});
    const [keys, setKeys] = useState({
        itemTypeId: '0'
    });

    // STORE STATE
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const timelineTypeDetails = useSelector(
        (state) => state.entities.directusService.timelineTypeDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const { id, roles, firstName, lastName } = useSelector(
        (state) => state.entities.userService.loggedInUser
    );

    // USE EFFECTS
    useEffect(() => {
        timelineTypeDetails?.length < 1 && dispatch(loadTimelineTypeDetails());
    }, []);

    useEffect(() => {
        if (timelineTypeDetails?.length && arrayTimelineTypes?.length === 0) {
            setArrayTimelineTypes(
                getConfiguredItems(timelineTypeDetails, currentParticipant.contractId)
            );
        }
    }, [timelineTypeDetails]);

    useEffect(() => {
        return () => {
            setNewEntry((prev) => ({ ...prev, participantId: currentParticipant.id }));
        };
    }, [currentParticipant]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === `Timeline entry has been added`) {
            setIsUpdateButtonDisabled(false);
            clearData();
        }
    }, [successMessage]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const clearData = () => {
        setNewEntry(initialState);
        setKeys(clearKeys(keys));
        setErrors({});
        onOpenTimelineEditor(false);
        isUpdateButtonDisabled && setIsUpdateButtonDisabled(false);
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onClose = (e, reason) => {
        if (reason === 'backdropClick') return;
        clearData();
    };

    const onCancel = () => clearData();

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry);
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;
        const validTime = newEntry.eventTime.substring(0, 2) + newEntry.eventTime.substring(3);
        const eventStatusId = timelineStatusDetails.find(
            (item) => item.name.toLowerCase() === 'attended'
        )?.id;

        const payload = {
            ...newEntry,
            eventStatusId,
            eventTime: validTime,
            eventType: 'Communication',
            participantId: currentParticipant.id,
            userId: id
        };
        dispatch(createParticipantTimelineEvent(payload));
    };

    return (
        <Dialog
            className="muiDialogWrapperMedium"
            open={isOpen}
            onClose={onClose}
            aria-labelledby="form-dialog-title">
            <div className={modal.modalHeader}>
                <DialogTitle id="form-dialog-title">Add Timeline</DialogTitle>
            </div>
            <div className={modal.modalContent}>
                <form onSubmit={onSubmit} className={form.formWrapper} data-testid="form_start">
                    <SingleSelect
                        id={'itemTypeId'}
                        key={keys.itemTypeId}
                        label={'Item Type'}
                        placeholder="Select item type..."
                        mandatory={true}
                        disabled={!hasRole(acceptedRoles, roles)}
                        menuItems={arrayTimelineTypes || []}
                        selectedId={newEntry.itemTypeId || ''}
                        selected={
                            arrayTimelineTypes.find((el) => el.id === newEntry?.itemTypeId) || {}
                        }
                        error={errors.itemTypeId}
                        onChange={(chosenId) => onUpdate('itemTypeId', chosenId)}
                    />
                    <h4>Adviser/Coach</h4>
                    <p className={classes.adviserName}>{firstName + ' ' + lastName}</p>
                    <TextAreaField
                        id="notes"
                        label="Notes"
                        placeholder={'Notes'}
                        disabled={!hasRole(acceptedRoles, roles)}
                        mandatory={true}
                        maxLength={NOTE_CHAR_LIMIT}
                        value={newEntry.notes || ''}
                        count={`${newEntry.notes.length}/${NOTE_CHAR_LIMIT}`}
                        error={errors.notes}
                        onChange={(e) => onUpdate('notes', e.target.value)}
                    />
                    <div className={`${form.formColumnSplit}`}>
                        <DateSelect
                            id="eventDate"
                            label="Date of Event"
                            disabled={!hasRole(acceptedRoles, roles)}
                            mandatory={true}
                            value={newEntry.eventDate}
                            error={errors.eventDate}
                            onDateChange={(date) => onUpdate('eventDate', date)}
                        />
                        <TimeSelect
                            id="eventTime"
                            label="Time of Event"
                            disabled={!hasRole(acceptedRoles, roles)}
                            mandatory={true}
                            max={'23:59'}
                            value={newEntry.eventTime}
                            error={errors.eventTime}
                            onTimeChange={(e) => onUpdate('eventTime', e.target.value)}
                        />
                    </div>
                    <div className={modal.actionButtons}>
                        <Button
                            id="statusEditorUpdateButton"
                            disabled={!hasRole(acceptedRoles, roles) || isUpdateButtonDisabled}
                            content="Update"
                        />
                        <Button
                            id="statusEditorCancelButton"
                            type="button"
                            content="Cancel"
                            onClick={onCancel}
                        />
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

TimelineEditor.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    acceptedRoles: PropTypes.arrayOf(PropTypes.string.isRequired),
    onOpenTimelineEditor: PropTypes.func,
    timelineStatusDetails: PropTypes.array.isRequired
};

export default TimelineEditor;
